import React from "react";
import { Link } from "react-router";
import { KeyRound, LoaderCircle } from "lucide-react";

import {
  getSupabaseSession,
  isSupabaseConfigured,
  signOutSupabaseSession,
  supabase,
} from "../lib/supabaseClient.js";
import PublicFeatureBanner from "./PublicFeatureBanner.jsx";
import "./LoginPage.css";

const primaryButtonClassName = "public-login__button public-login__button--primary";

const landscapeSrc = import.meta.env.DEV
  ? `${import.meta.env.BASE_URL}brand/landing/montreal-truck-photo-selection.png`
  : "/brand/landing/montreal-truck-photo-selection.png";

function resolveResetErrorMessage(error) {
  const message = error instanceof Error && typeof error.message === "string"
    ? error.message.trim()
    : error && typeof error === "object" && typeof error.message === "string"
      ? error.message.trim()
      : "";

  if (!message) {
    return "La réinitialisation du mot de passe a échoué.";
  }
  if (message.toLowerCase().includes("same password")) {
    return "Choisissez un mot de passe différent de l’actuel.";
  }
  return message;
}

export default function ResetPasswordPage() {
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState("");
  const [infoMessage, setInfoMessage] = React.useState("");
  const [isSessionReady, setIsSessionReady] = React.useState(false);
  const [hasRecoverySession, setHasRecoverySession] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSuccess, setIsSuccess] = React.useState(false);
  const supabaseConfigured = isSupabaseConfigured();

  React.useEffect(() => {
    if (typeof document !== "undefined") {
      document.body.classList.remove("page-shell", "results-page");
      document.body.style.background = "";
      document.documentElement.style.background = "";
    }

    let isActive = true;

    async function syncSessionState() {
      const { error, session } = await getSupabaseSession();
      if (!isActive) {
        return;
      }

      if (error) {
        setErrorMessage(resolveResetErrorMessage(error));
        setIsSessionReady(true);
        setHasRecoverySession(false);
        return;
      }

      setHasRecoverySession(Boolean(session));
      setIsSessionReady(true);
      if (session && !isSuccess) {
        setInfoMessage((currentMessage) => currentMessage || "Lien validé. Choisissez maintenant un nouveau mot de passe.");
      }
    }

    let unsubscribe = () => {};
    if (supabase) {
      const { data } = supabase.auth.onAuthStateChange((event, session) => {
        if (!isActive) {
          return;
        }

        if (event === "PASSWORD_RECOVERY") {
          setInfoMessage("Lien validé. Choisissez maintenant un nouveau mot de passe.");
        }
        setHasRecoverySession(Boolean(session));
        setIsSessionReady(true);
      });

      unsubscribe = () => {
        data.subscription.unsubscribe();
      };
    }

    void syncSessionState();

    return () => {
      isActive = false;
      unsubscribe();
    };
  }, [isSuccess]);

  async function handleSubmit(event) {
    event.preventDefault();
    setErrorMessage("");
    setInfoMessage("");

    if (!supabase) {
      setErrorMessage("La réinitialisation est momentanément indisponible. Veuillez contacter votre administrateur.");
      return;
    }

    const resolvedPassword = password.trim();
    const resolvedConfirmPassword = confirmPassword.trim();
    if (resolvedPassword.length < 8) {
      setErrorMessage("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (resolvedPassword !== resolvedConfirmPassword) {
      setErrorMessage("Les deux champs de mot de passe doivent correspondre.");
      return;
    }
    if (!hasRecoverySession) {
      setErrorMessage("Le lien de réinitialisation n’est plus actif. Redemandez un nouvel e-mail.");
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase.auth.updateUser({ password: resolvedPassword });
      if (error) {
        setErrorMessage(resolveResetErrorMessage(error));
        return;
      }

      try {
        await signOutSupabaseSession();
      } catch {}

      setPassword("");
      setConfirmPassword("");
      setHasRecoverySession(false);
      setIsSuccess(true);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="public-login">
      <header className="public-login__header">
        <div className="public-login__header-inner">
          <Link className="public-login__button public-login__button--secondary public-login__back" to="/login">
            Retour à la connexion
          </Link>
        </div>
      </header>

      <main className="public-login__main">
        <section className="public-login__card" aria-labelledby="reset-password-title">
          <p className="public-login__eyebrow">
            <KeyRound size={18} aria-hidden="true" />
            Accès sécurisé TransIA
          </p>
          <h1 id="reset-password-title" className="public-login__title font-display">Nouveau mot de passe</h1>
          <p className="public-login__intro">
            Saisissez votre nouveau mot de passe, puis confirmez-le.
          </p>

          {!supabaseConfigured ? (
            <div className="public-login__alert public-login__alert--warning" role="status">
              La réinitialisation est momentanément indisponible. Veuillez contacter votre administrateur.
            </div>
          ) : null}

          {errorMessage ? (
            <div id="reset-password-error" className="public-login__alert public-login__alert--error" role="alert">
              {errorMessage}
            </div>
          ) : null}

          {!isSuccess && !errorMessage && infoMessage && hasRecoverySession ? (
            <div className="public-login__alert public-login__alert--success" role="status">
              {infoMessage}
            </div>
          ) : null}

          {isSuccess ? (
            <div className="public-login__form">
              <div className="public-login__alert public-login__alert--success" role="status">
                <p><strong>Mot de passe mis à jour</strong></p>
                <p>Vous pouvez maintenant vous reconnecter avec votre nouveau mot de passe.</p>
              </div>
              <Link className={primaryButtonClassName} to="/login">
                Retour à la connexion
              </Link>
            </div>
          ) : (
            <form className="public-login__form" onSubmit={handleSubmit} aria-busy={isSubmitting} aria-describedby={errorMessage ? "reset-password-error" : undefined}>
              <label className="public-login__field">
                <span>Nouveau mot de passe</span>
                <input
                  autoComplete="new-password"
                  disabled={isSubmitting || !supabaseConfigured || !hasRecoverySession}
                  minLength={8}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Minimum 8 caractères"
                  type="password"
                  value={password}
                />
              </label>

              <label className="public-login__field">
                <span>Confirmation</span>
                <input
                  autoComplete="new-password"
                  disabled={isSubmitting || !supabaseConfigured || !hasRecoverySession}
                  minLength={8}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  placeholder="Répétez le mot de passe"
                  type="password"
                  value={confirmPassword}
                />
              </label>

              {supabaseConfigured && !isSessionReady ? (
                <p className="public-login__note" role="status">Vérification du lien en cours…</p>
              ) : null}

              {supabaseConfigured && isSessionReady && !hasRecoverySession ? (
                <div className="public-login__alert public-login__alert--warning" role="status">
                  Ce lien est absent ou n’est plus actif. Demandez un nouveau courriel de réinitialisation à votre administrateur.
                </div>
              ) : null}

              <button
                className={primaryButtonClassName}
                disabled={isSubmitting || !supabaseConfigured || !hasRecoverySession}
                type="submit"
              >
                {isSubmitting ? <LoaderCircle className="animate-spin" aria-hidden="true" size={16} /> : null}
                {isSubmitting ? "Enregistrement en cours…" : "Enregistrer le nouveau mot de passe"}
              </button>
            </form>
          )}
        </section>

        <div className="public-login__landscape" aria-hidden="true">
          <img src={landscapeSrc} alt="" width={1672} height={941} fetchpriority="high" loading="eager" decoding="async" />
        </div>
      </main>
      <PublicFeatureBanner />
    </div>
  );
}
